import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '../components/AppShell'
import { useEntityList, useEntitySave, useEntityDelete, makeEntity, now } from '../api/hooks/useEntity'
import type { Subject } from '../types/entities'
import { ENTITY } from '../types/entities'
import { Dialog } from '../components/Dialog'
import { Field } from '../components/Field'

const PALETTE = ['#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#22c55e', '#f97316', '#06b6d4', '#a855f7']

/** Управление списком предметов учителя. */
export function SubjectsPage() {
  const { t } = useTranslation()
  const list = useEntityList<Subject>(ENTITY.subject)
  const [editing, setEditing] = useState<Subject | null>(null)
  const subjects = (list.data ?? []).sort((a, b) => a.name.localeCompare(b.name))

  return (
    <AppShell
      title={t('subjectsPage.title')}
      subtitle={t('subjectsPage.subtitle')}
      actions={
        <button onClick={() => setEditing(blank())} className="btn-primary text-sm">
          {t('subjectsPage.addSubject')}
        </button>
      }
    >
      {list.isLoading ? (
        <div className="text-center py-10 text-gray-500">{t('common.loading')}</div>
      ) : subjects.length === 0 ? (
        <Empty onAdd={() => setEditing(blank())} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {subjects.map((s) => (
            <button
              key={s.id}
              onClick={() => setEditing(s)}
              className="group flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:border-primary/40 hover:shadow-md transition-all text-left"
            >
              <div
                className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center text-white font-bold"
                style={{ background: s.color ?? '#7c3aed' }}
              >
                {(s.shortName ?? s.name).slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 group-hover:text-primary truncate">{s.name}</div>
                {s.shortName && <div className="text-xs text-gray-500">{s.shortName}</div>}
              </div>
              <span className="text-gray-300">›</span>
            </button>
          ))}
        </div>
      )}

      {editing && <SubjectForm initial={editing} onClose={() => setEditing(null)} />}
    </AppShell>
  )
}

function SubjectForm({ initial, onClose }: { initial: Subject; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useEntitySave<Subject>(ENTITY.subject)
  const del = useEntityDelete<Subject>(ENTITY.subject)
  const [name, setName] = useState(initial.name)
  const [shortName, setShortName] = useState(initial.shortName ?? '')
  const [color, setColor] = useState(initial.color ?? PALETTE[0])

  const isNew = !initial.id

  const handleSave = async () => {
    if (!name.trim()) return
    await save.mutateAsync(makeEntity<Subject>({
      ...initial,
      name: name.trim(),
      shortName: shortName.trim() || undefined,
      color,
      updatedAt: now(),
    }))
    onClose()
  }

  const handleDelete = async () => {
    if (!confirm(t('subjectsPage.deleteConfirm', { name }))) return
    await del.mutateAsync(initial)
    onClose()
  }

  return (
    <Dialog title={isNew ? t('subjectsPage.dialogNewTitle') : t('subjectsPage.dialogEditTitle')} onClose={onClose}>
      <Field label={t('subjectsPage.fieldName')}>
        <input
          className="input-field"
          placeholder={t('subjectsPage.fieldNamePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </Field>
      <Field label={t('subjectsPage.fieldShortName')}>
        <input
          className="input-field"
          placeholder={t('subjectsPage.fieldShortNamePlaceholder')}
          value={shortName}
          onChange={(e) => setShortName(e.target.value)}
        />
      </Field>
      <Field label={t('subjectsPage.fieldColor')}>
        <div className="flex gap-2 flex-wrap">
          {PALETTE.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setColor(p)}
              className={`w-9 h-9 rounded-lg transition-transform ${color === p ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-110'}`}
              style={{ background: p }}
            />
          ))}
        </div>
      </Field>

      <div className="flex gap-2 justify-between flex-wrap mt-4">
        <div>
          {!isNew && (
            <button onClick={handleDelete} disabled={del.isPending} className="text-sm text-red-600 hover:text-red-700 px-3 py-2">
              {t('common.delete')}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-outline text-sm">{t('common.cancel')}</button>
          <button onClick={handleSave} disabled={save.isPending || !name.trim()} className="btn-primary text-sm">
            {save.isPending ? t('subjectsPage.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

function Empty({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="text-center py-12 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
      <div className="text-5xl mb-3">📚</div>
      <h3 className="font-semibold text-gray-900 mb-2">{t('subjectsPage.emptyTitle')}</h3>
      <p className="text-sm text-gray-600 mb-4 px-4">
        {t('subjectsPage.emptyText')}
      </p>
      <button onClick={onAdd} className="btn-primary text-sm">{t('subjectsPage.addSubject')}</button>
    </div>
  )
}

function blank(): Subject {
  return { id: '', name: '', shortName: undefined, color: PALETTE[0], createdAt: 0, updatedAt: 0 }
}
